class ContainerStoreHeader extends HTMLElement {
  #events;
  #request;
  #timer;
  #resize;
  #hoverOpen;
  #hoverClose;
  #sequence = 0;

  connectedCallback() {
    this.#start();
  }

  updatedCallback() {
    this.#stop();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
  }

  #stop() {
    this.#events?.abort();
    this.#request?.abort();
    clearTimeout(this.#timer);
    this.#clearHover();
    this.#resize?.disconnect();
    this.#sequence++;
  }

  #start() {
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    this.dataset.enhanced = '';
    this.querySelector('.ch__menu-toggle')?.addEventListener('click', () => this.#menu(!this.hasAttribute('data-menu-open')), options);
    this.querySelector('.ch__actions')?.addEventListener('click', () => this.#closeAll(), options);
    this.querySelectorAll('.ch__category').forEach((details) => details.addEventListener('toggle', () => {
      if (!details.open) return;
      this.querySelectorAll('.ch__category').forEach((other) => { if (other !== details) other.open = false; });
      this.#hideSearch();
    }, options));
    this.querySelectorAll('.ch__category').forEach((details) => {
      details.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse' || !window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches) return;
        clearTimeout(this.#hoverClose);
        if (details.open || this.#hoverOpen) return;
        this.#clearHover();
        this.#hoverOpen = setTimeout(() => {
          this.#hoverOpen = undefined;
          this.querySelectorAll('.ch__category').forEach((other) => { other.open = other === details; });
          this.#hideSearch();
        }, 100);
      }, options);
      details.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse' || window.innerWidth < 990) return;
        this.#clearHover();
        this.#hoverClose = setTimeout(() => {
          if (details.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) return;
          details.open = false;
        }, 220);
      }, options);
      details.querySelector('summary').addEventListener('click', () => this.#clearHover(), options);
      details.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !details.contains(event.relatedTarget) && !details.matches(':hover')) details.open = false;
      }, options);
    });
    const input = this.querySelector('input[type="search"]');
    input?.addEventListener('input', () => {
      clearTimeout(this.#timer);
      this.#request?.abort();
      this.#sequence++;
      this.#hideSearch();
      if (input.value.trim().length >= 2) this.#timer = setTimeout(() => this.#search(input.value.trim()), 220);
    }, options);
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' && input.getAttribute('aria-expanded') === 'true') {
        const first = this.querySelector('.ch__results a');
        if (first) { event.preventDefault(); first.focus(); }
      }
    }, options);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.#clearHover();
      if (!this.querySelector('.ch__suggestions').hidden) {
        this.#hideSearch(); input.focus();
      } else {
        const openGroup = this.querySelector('.ch__category[open]');
        if (openGroup) { openGroup.open = false; openGroup.querySelector('summary').focus(); }
        else this.#menu(false, true);
      }
      event.preventDefault();
    }, options);
    document.addEventListener('click', (event) => {
      if (this.contains(event.target)) return;
      this.#closeAll();
    }, options);
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.#closeAll();
    }, options);
    this.#resize = new ResizeObserver(() => {
      const height = this.offsetHeight;
      document.body.style.setProperty('--header-height', `${height}px`);
      document.body.style.setProperty('--header-group-height', `${height}px`);
      if (window.innerWidth >= 990) this.#menu(false);
    });
    this.#resize.observe(this);
  }

  #menu(open, restore = false) {
    this.#clearHover();
    this.toggleAttribute('data-menu-open', open);
    const button = this.querySelector('.ch__menu-toggle');
    button?.setAttribute('aria-expanded', String(open));
    if (restore) button?.focus();
    if (!open) this.querySelectorAll('.ch__category').forEach((item) => { item.open = false; });
  }

  #clearHover() {
    clearTimeout(this.#hoverOpen);
    clearTimeout(this.#hoverClose);
    this.#hoverOpen = undefined;
    this.#hoverClose = undefined;
  }

  #closeAll() {
    this.#menu(false);
    this.#hideSearch();
  }

  #hideSearch() {
    this.querySelector('.ch__suggestions').hidden = true;
    this.querySelector('input[type="search"]').setAttribute('aria-expanded', 'false');
    this.#request?.abort();
    clearTimeout(this.#timer);
    this.#sequence++;
  }

  async #search(query) {
    const sequence = ++this.#sequence;
    this.#request = new AbortController();
    const status = this.querySelector('.ch__search-status');
    status.textContent = this.dataset.loading;
    const url = new URL(`${this.dataset.searchUrl}.json`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '5');
    try {
      const response = await fetch(url, { signal: this.#request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search unavailable');
      const data = await response.json();
      if (sequence !== this.#sequence) return;
      const results = data.resources?.results?.products || [];
      const list = this.querySelector('.ch__results');
      list.replaceChildren();
      for (const product of results) {
        const li = document.createElement('li');
        const link = document.createElement('a');
        const destination = new URL(product.url, window.location.origin);
        if (destination.origin !== window.location.origin) continue;
        link.href = destination.href;
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image; image.alt = ''; image.width = 50; image.height = 50;
          link.append(image);
        }
        const title = document.createElement('span');
        title.textContent = product.title;
        link.append(title); li.append(link); list.append(li);
      }
      if (!results.length) {
        const li = document.createElement('li'); const p = document.createElement('p');
        p.textContent = this.dataset.noResults; li.append(p); list.append(li);
      }
      const all = this.querySelector('.ch__view-results');
      const searchUrl = new URL(this.querySelector('form').action);
      searchUrl.searchParams.set('q', query); searchUrl.searchParams.set('type', 'product');
      all.href = searchUrl.href;
      this.querySelector('.ch__suggestions').hidden = false;
      this.querySelector('input[type="search"]').setAttribute('aria-expanded', 'true');
      status.textContent = results.length ? this.dataset.suggestions : this.dataset.noResults;
    } catch (error) {
      if (error.name !== 'AbortError' && sequence === this.#sequence) status.textContent = this.dataset.searchError;
    }
  }
}

if (!customElements.get('container-store-header')) customElements.define('container-store-header', ContainerStoreHeader);
